import React, { useEffect, useState } from "react";
import { Loader2, Clock, Check, X, PlugZap, AlertTriangle, ZoomIn, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";
import { adminAxios } from "../../context/AdminAuth";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const StatusBadge = ({ status }) => {
  const map = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-[#E4EDE6] text-[#2F5E43] border-[#2F5E43]/20",
    rejected: "bg-red-50 text-red-700 border-red-200",
  };
  const cls = map[status] || "bg-gray-50 text-gray-600 border-gray-200";
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${cls}`}>
      {status || "unknown"}
    </span>
  );
};

const DocThumb = ({ url, label, onOpen }) => {
  const [state, setState] = useState("loading"); // loading | ok | placeholder | error

  if (!url) {
    return (
      <div className="flex flex-col items-center gap-1" data-testid="doc-none">
        <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-dashed border-[#E8EBE9] bg-[#FBFBF9] text-[10px] text-[#8AA583]">
          none
        </div>
        <span className="text-[10px] font-medium text-[#8AA583]">{label}</span>
      </div>
    );
  }

  const flagged = state === "placeholder" || state === "error";
  return (
    <div className="flex flex-col items-center gap-1" data-testid="doc-thumb">
      <button
        type="button"
        onClick={() => onOpen(url, label)}
        title={`View ${label} full size`}
        className="group relative block"
        data-testid="doc-thumb-btn"
      >
        <img
          src={url}
          alt={label}
          onLoad={(e) => {
            const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
            setState(w <= 2 && h <= 2 ? "placeholder" : "ok");
          }}
          onError={() => setState("error")}
          className={`h-16 w-16 rounded-lg border object-cover transition-opacity ${
            flagged ? "border-amber-300 opacity-30" : "border-[#E8EBE9] group-hover:opacity-80"
          }`}
        />
        {!flagged && (
          <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 opacity-0 transition-all group-hover:bg-black/30 group-hover:opacity-100">
            <ZoomIn className="h-5 w-5 text-white" />
          </span>
        )}
      </button>
      <span className="text-[10px] font-medium text-[#8AA583]">{label}</span>
      {state === "placeholder" && (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700"
          title="The app sent a 1×1 placeholder instead of a real image."
          data-testid="doc-placeholder-badge"
        >
          <AlertTriangle className="h-3 w-3" /> Placeholder
        </span>
      )}
      {state === "error" && (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700" data-testid="doc-error-badge">
          <AlertTriangle className="h-3 w-3" /> Unreadable
        </span>
      )}
    </div>
  );
};

const Lightbox = ({ item, onClose }) => {
  const [fit, setFit] = useState(true); // true = fit-to-screen, false = actual size / zoom

  useEffect(() => {
    setFit(true);
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [item, onClose]);

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex flex-col bg-black/80 backdrop-blur-sm"
      data-testid="lightbox"
      onClick={onClose}
    >
      <div className="flex items-center justify-between px-6 py-4 text-white" onClick={(e) => e.stopPropagation()}>
        <span className="font-heading text-lg font-semibold">{item.label}</span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setFit((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/25 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-white/10"
            data-testid="lightbox-fit-toggle"
          >
            {fit ? <><Maximize2 className="h-4 w-4" /> Actual size</> : <><Minimize2 className="h-4 w-4" /> Fit to screen</>}
          </button>
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-white/25"
            data-testid="lightbox-close"
          >
            <X className="h-4 w-4" /> Close
          </button>
        </div>
      </div>
      <div className="flex flex-1 items-center justify-center overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
        <img
          src={item.url}
          alt={item.label}
          className={fit ? "max-h-[80vh] max-w-[90vw] object-contain" : "max-w-none cursor-zoom-out"}
          style={fit ? {} : { imageRendering: "auto" }}
          onClick={() => setFit((v) => !v)}
          data-testid="lightbox-image"
        />
      </div>
    </div>
  );
};

const RejectModal = ({ open, onCancel, onConfirm, busy }) => {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4" data-testid="reject-modal" onClick={onCancel}>
      <div className="w-full max-w-md rounded-3xl border border-[#E8EBE9] bg-white p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-xl font-semibold text-[#111E16]">Reject verification</h3>
        <p className="mt-1.5 text-sm text-[#4A5F52]">
          The user will be notified and their account kept inactive. Add an optional reason (shared for your records).
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Reason (optional) — e.g. document unclear"
          className="mt-4 w-full resize-none rounded-xl border border-[#E8EBE9] px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#2F5E43]/30"
          data-testid="reject-reason-input"
        />
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="rounded-full border border-[#E8EBE9] px-5 py-2.5 text-sm font-medium text-[#4A5F52] hover:bg-[#F2F5F2]" data-testid="reject-cancel">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason.trim())}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-full bg-red-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
            data-testid="reject-confirm"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Confirm rejection
          </button>
        </div>
      </div>
    </div>
  );
};

export const VerificationTab = () => {
  const [configured, setConfigured] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [rejectId, setRejectId] = useState(null);

  const load = () => {
    setLoading(true);
    adminAxios
      .get("/admin/verification-requests")
      .then((r) => setRows(Array.isArray(r.data) ? r.data : r.data?.requests || []))
      .catch((e) => {
        if (e.response?.status !== 503) {
          toast.error(e.response?.data?.detail || "Could not load verification requests.");
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    adminAxios
      .get("/admin/verification-config")
      .then((r) => {
        setConfigured(r.data.configured);
        if (r.data.configured) load();
      })
      .catch(() => setConfigured(false));
  }, []);

  const approve = async (id) => {
    setActing(`${id}:approve`);
    try {
      const res = await adminAxios.post(`/admin/verification-requests/${id}/approve`);
      toast.success(res.data?.message || "Approved — the user's account has been activated.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not approve the request.");
    } finally {
      setActing(null);
    }
  };

  const confirmReject = async (reason) => {
    const id = rejectId;
    setActing(`${id}:reject`);
    try {
      const res = await adminAxios.post(`/admin/verification-requests/${id}/reject`, {
        rejection_reason: reason || null,
      });
      toast.success(res.data?.message || "Rejected — the user has been notified.");
      setRejectId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reject the request.");
    } finally {
      setActing(null);
    }
  };

  if (configured === null) {
    return (
      <div className="flex items-center justify-center py-20 text-[#4A5F52]">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Checking connection…
      </div>
    );
  }

  if (!configured) {
    return (
      <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-[#2F5E43]/20 bg-white py-20 text-center" data-testid="verification-inactive">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E4EDE6] text-[#2F5E43]">
          <PlugZap className="h-6 w-6" />
        </span>
        <h3 className="font-heading mt-5 text-xl font-semibold text-[#111E16]">Verification — Not connected yet</h3>
        <p className="mt-2 max-w-md text-[#4A5F52]">
          This tab will list app identity-verification requests once the SisterSafe app backend is connected.
        </p>
        <span className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
          <Clock className="h-3.5 w-3.5" /> Awaiting app API configuration
        </span>
      </div>
    );
  }

  return (
    <div data-testid="verification-active">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-[#4A5F52]">{rows.length} verification requests</p>
        <button onClick={load} className="rounded-full border border-[#E8EBE9] bg-white px-4 py-2 text-sm font-medium text-[#2F5E43] hover:bg-[#F2F5F2]" data-testid="verification-refresh">
          Refresh
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-[#E8EBE9] bg-white">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#4A5F52]">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-20 text-center text-[#8AA583]">No verification requests.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#F2F5F2] text-xs uppercase tracking-wider text-[#8AA583]">
                <tr>
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold">Email</th>
                  <th className="px-5 py-3 font-semibold">Documents</th>
                  <th className="px-5 py-3 font-semibold">Submitted</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0F2F0]">
                {rows.map((r) => {
                  const id = r.id;
                  const isPending = (r.status || "pending") === "pending";
                  return (
                    <tr key={id} className="hover:bg-[#FBFBF9]" data-testid="verification-row">
                      <td className="px-5 py-4 font-medium text-[#111E16]">{r.full_name || r.name}</td>
                      <td className="px-5 py-4 text-[#4A5F52]">{r.email}</td>
                      <td className="px-5 py-4">
                        {isPending ? (
                          <div className="flex gap-4">
                            <DocThumb url={r.selfie_url} label="Selfie" onOpen={(url, label) => setLightbox({ url, label })} />
                            <DocThumb url={r.id_document_url} label="Photo ID" onOpen={(url, label) => setLightbox({ url, label })} />
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs text-[#8AA583]" data-testid="docs-removed">
                            <Check className="h-3.5 w-3.5" /> Documents removed after review
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-[#8AA583]">{fmtDate(r.submitted_at)}</td>
                      <td className="px-5 py-4"><StatusBadge status={r.status} /></td>
                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            disabled={!isPending || acting === `${id}:approve`}
                            onClick={() => approve(id)}
                            className="inline-flex items-center gap-1 rounded-full bg-[#2F5E43] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#244A34] disabled:cursor-not-allowed disabled:opacity-40"
                            data-testid="verification-approve"
                          >
                            {acting === `${id}:approve` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                            Approve
                          </button>
                          <button
                            disabled={!isPending || acting === `${id}:reject`}
                            onClick={() => setRejectId(id)}
                            className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                            data-testid="verification-reject"
                          >
                            <X className="h-3.5 w-3.5" /> Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Lightbox item={lightbox} onClose={() => setLightbox(null)} />
      <RejectModal
        open={rejectId !== null}
        busy={acting === `${rejectId}:reject`}
        onCancel={() => setRejectId(null)}
        onConfirm={confirmReject}
      />
    </div>
  );
};
